// RevenueReport.jsx - Updated with Smart Date Handling
import React, { useState, useEffect } from 'react';
import {
  FaRupeeSign,
  FaHotel,
  FaUtensils,
  FaSpa,
  FaConciergeBell,
  FaWallet,
  FaCreditCard,
  FaMobileAlt,
  FaMoneyBillWave,
  FaTimes,
  FaPercent,
  FaChartLine,
  FaDownload,
  FaEnvelope,
  FaPrint,
  FaCalendarAlt,
  FaSync,
  FaSpinner,
  FaBuilding,
  FaClock,
  FaArrowUp,
  FaArrowDown,
  FaDollarSign,
  FaCoffee,
  FaTrophy,
  FaChartPie,
  FaFilePdf,
  FaFileExcel,
  FaUser,
  FaBed,
  FaChartBar,
  FaPlus,
  FaList,
  FaFilter,
  FaCheck,
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
} from 'react-icons/fa';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Blue & Gold Theme
const COLORS = {
  primary: '#0A1628',
  secondary: '#1A2A4A',
  gold: '#C9A84C',
  goldLight: '#E8D5A3',
  navy: '#0F1A3A',
  navyLight: '#1B2F5C',
  white: '#FFFFFF',
  lightBg: '#F5F7FA',
  border: '#E4E8F0',
  textPrimary: '#0A1628',
  textSecondary: '#4A5F7A',
  textMuted: '#6B7FA0',
  success: '#10B981',
  danger: '#EF4444',
};

// Chart colors for all categories
const CHART_COLORS = ['#0A1628', '#C9A84C', '#1A2A4A', '#2A4580', '#D4AF37', '#6B7FA0'];

// Billing Categories
const BILLING_CATEGORIES = [
  'All',
  'Room Rent',
  'Food Order',
  'Amenity Service',
  'Room Service',
  'Enhance stay reservation',
  'Enhance stay modification',
  'Restaurant POS',
];

// Payment Methods
const PAYMENT_METHODS = ['All', 'Cash', 'Card', 'UPI', 'Netbanking', 'Razorpay'];

// Booking Types
const BOOKING_TYPES = ['All', 'Walkin', 'Online'];

const RevenueReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [emailGeneratedBy, setEmailGeneratedBy] = useState('');
  const [emailPeriod, setEmailPeriod] = useState('monthly');
  const [emailYear, setEmailYear] = useState(new Date().getFullYear());
  const [emailMonth, setEmailMonth] = useState(new Date().getMonth() + 1);
  const [emailStartDate, setEmailStartDate] = useState('');
  const [emailEndDate, setEmailEndDate] = useState('');
  const [emailBillingCategory, setEmailBillingCategory] = useState('');
  const [emailPaymentMethod, setEmailPaymentMethod] = useState('');
  const [emailBookingType, setEmailBookingType] = useState('');
  const [emailCcHotel, setEmailCcHotel] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPeriod, setPdfPeriod] = useState('monthly');
  const [pdfYear, setPdfYear] = useState(new Date().getFullYear());
  const [pdfMonth, setPdfMonth] = useState(new Date().getMonth() + 1);
  const [pdfStartDate, setPdfStartDate] = useState('');
  const [pdfEndDate, setPdfEndDate] = useState('');
  // Excel Modal states
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelPeriod, setExcelPeriod] = useState('monthly');
  const [excelYear, setExcelYear] = useState(new Date().getFullYear());
  const [excelMonth, setExcelMonth] = useState(new Date().getMonth() + 1);
  const [excelStartDate, setExcelStartDate] = useState('');
  const [excelEndDate, setExcelEndDate] = useState('');
  const [excelBillingCategory, setExcelBillingCategory] = useState('');
  const [excelPaymentMethod, setExcelPaymentMethod] = useState('');
  const [excelBookingType, setExcelBookingType] = useState('');
  const [propertyId, setPropertyId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  // Add state for custom dates
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Add state for daily and weekly dates
  const [dailyDate, setDailyDate] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  // Auto-update flag to prevent unnecessary API calls
  const [shouldFetch, setShouldFetch] = useState(false);
  const [emailExportFormat, setEmailExportFormat] = useState('pdf');

  // Filter states for main view
  const [selectedBillingCategory, setSelectedBillingCategory] = useState('All');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('All');
  const [selectedBookingType, setSelectedBookingType] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
          headers: { accept: 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.property_id) setPropertyId(data.property_id);
          if (data.email) {
            setUserEmail(data.email);
            setEmailGeneratedBy(data.email);
          }
          if (data.name) setUserName(data.name);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  // Check if all required dates are filled for the selected period
  const isDateValid = () => {
    if (selectedPeriod === 'monthly') return true;
    if (selectedPeriod === 'yearly') return true;
    if (selectedPeriod === 'daily') return dailyDate !== '';
    if (selectedPeriod === 'weekly') return weekStartDate !== '' && weekEndDate !== '';
    if (selectedPeriod === 'custom') return startDate !== '' && endDate !== '';
    return false;
  };

  // Updated fetchRevenueReport function - Only fetch when dates are valid
  const fetchRevenueReport = async () => {
    // Don't fetch if dates are invalid
    if (!isDateValid()) {
      toast.warning('Please fill in all required date fields');
      return;
    }

    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/revenue`;

      // Build query params
      const params = new URLSearchParams();

      if (selectedPeriod === 'monthly' && month) {
        params.append('year', year);
        params.append('month', month);
      } else if (selectedPeriod === 'yearly') {
        // For yearly, send year and also start_date/end_date for the full year
        params.append('year', year);
        // Auto-calculate year range
        const startOfYear = `${year}-01-01`;
        const endOfYear = `${year}-12-31`;
        params.append('start_date', startOfYear);
        params.append('end_date', endOfYear);
      } else if (selectedPeriod === 'daily') {
        if (dailyDate) {
          params.append('start_date', dailyDate);
          params.append('end_date', dailyDate);
        }
      } else if (selectedPeriod === 'weekly') {
        if (weekStartDate && weekEndDate) {
          params.append('start_date', weekStartDate);
          params.append('end_date', weekEndDate);
        }
      } else if (selectedPeriod === 'custom') {
        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        }
      }

      // Add filters
      if (selectedBillingCategory && selectedBillingCategory !== 'All') {
        params.append('billing_category', selectedBillingCategory);
      }
      if (selectedPaymentMethod && selectedPaymentMethod !== 'All') {
        params.append('payment_method', selectedPaymentMethod);
      }
      if (selectedBookingType && selectedBookingType !== 'All') {
        params.append('booking_type', selectedBookingType);
      }

      if (propertyId) params.append('property_id', propertyId);
      if (userEmail) params.append('email', userEmail);

      // Append params to URL
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      console.log('Fetching URL:', url); // Debug log

      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReportData(data.data);
          toast.success('Report loaded successfully!');
        }
      } else {
        throw new Error('Failed to fetch revenue report');
      }
    } catch (error) {
      console.error('Error fetching revenue report:', error);
      toast.error('Error loading revenue report');
    } finally {
      setLoading(false);
      setShouldFetch(false);
    }
  };

  // Handle period change - reset dates and fetch immediately for monthly/yearly
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    setShouldFetch(false);

    // Reset date states based on period
    if (newPeriod === 'monthly' || newPeriod === 'yearly') {
      // Auto-fetch for monthly/yearly
      setTimeout(() => {
        fetchRevenueReport();
      }, 100);
    } else if (newPeriod === 'daily') {
      setDailyDate('');
    } else if (newPeriod === 'weekly') {
      setWeekStartDate('');
      setWeekEndDate('');
    } else if (newPeriod === 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  // Handle date changes - only set flag, don't fetch immediately
  const handleDateChange = (type, value) => {
    setShouldFetch(false);

    switch (type) {
      case 'daily':
        setDailyDate(value);
        break;
      case 'weekStart':
        setWeekStartDate(value);
        break;
      case 'weekEnd':
        setWeekEndDate(value);
        break;
      case 'customStart':
        setStartDate(value);
        break;
      case 'customEnd':
        setEndDate(value);
        break;
      default:
        break;
    }
  };

  // Handle applying date range - manual fetch trigger
  const handleApplyDateRange = () => {
    if (isDateValid()) {
      fetchRevenueReport();
    } else {
      toast.warning('Please fill in all required date fields');
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (propertyId !== null) {
      fetchRevenueReport();
    }
  }, [propertyId]);

  // Fetch when filters change (but not on date changes)
  useEffect(() => {
    if (propertyId !== null && isDateValid()) {
      fetchRevenueReport();
    }
  }, [selectedBillingCategory, selectedPaymentMethod, selectedBookingType]);

  // Separate effect for year/month changes (monthly/yearly auto-fetch)
  useEffect(() => {
    if (propertyId !== null && (selectedPeriod === 'monthly' || selectedPeriod === 'yearly')) {
      fetchRevenueReport();
    }
  }, [year, month, selectedPeriod]);

  // Excel Download Handler with Modal
  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/revenue/excel`;

      const params = new URLSearchParams();

      if (excelPeriod === 'monthly' && excelMonth) {
        params.append('year', excelYear);
        params.append('month', excelMonth);
      } else if (excelPeriod === 'yearly') {
        params.append('year', excelYear);
      } else if (excelPeriod === 'daily') {
        if (excelStartDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelStartDate);
        }
      } else if (excelPeriod === 'weekly') {
        if (excelStartDate && excelEndDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelEndDate);
        }
      } else if (excelPeriod === 'custom') {
        if (excelStartDate && excelEndDate) {
          params.append('start_date', excelStartDate);
          params.append('end_date', excelEndDate);
        }
      }

      // Add filters from Excel modal
      if (excelBillingCategory && excelBillingCategory !== 'All') {
        params.append('billing_category', encodeURIComponent(excelBillingCategory));
      }
      if (excelPaymentMethod && excelPaymentMethod !== 'All') {
        params.append('payment_method', encodeURIComponent(excelPaymentMethod));
      }
      if (excelBookingType && excelBookingType !== 'All') {
        params.append('booking_type', encodeURIComponent(excelBookingType));
      }

      if (propertyId) params.append('property_id', propertyId);

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `RevenueReport_${excelPeriod}_${new Date().toISOString().split('T')[0]}.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1];
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Excel report downloaded successfully!');
        setShowExcelModal(false);
      } else {
        throw new Error('Failed to download Excel report');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Failed to download Excel report');
    } finally {
      setLoading(false);
    }
  };

  // PDF Download Handler
  const handleDownloadPDF = async () => {
    setLoading(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/revenue/pdf`;

      const params = new URLSearchParams();
      params.append('generated_by', encodeURIComponent(userName || 'Hotel Manager'));

      if (pdfPeriod === 'monthly' && pdfMonth) {
        params.append('year', pdfYear);
        params.append('month', pdfMonth);
      } else if (pdfPeriod === 'yearly') {
        params.append('year', pdfYear);
      } else if (pdfPeriod === 'daily') {
        if (pdfStartDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfStartDate);
        }
      } else if (pdfPeriod === 'weekly') {
        if (pdfStartDate && pdfEndDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfEndDate);
        }
      } else if (pdfPeriod === 'custom') {
        if (pdfStartDate && pdfEndDate) {
          params.append('start_date', pdfStartDate);
          params.append('end_date', pdfEndDate);
        }
      }

      // Add filters for PDF
      if (selectedBillingCategory && selectedBillingCategory !== 'All') {
        params.append('billing_category', encodeURIComponent(selectedBillingCategory));
      }
      if (selectedPaymentMethod && selectedPaymentMethod !== 'All') {
        params.append('payment_method', encodeURIComponent(selectedPaymentMethod));
      }
      if (selectedBookingType && selectedBookingType !== 'All') {
        params.append('booking_type', encodeURIComponent(selectedBookingType));
      }

      if (propertyId) params.append('property_id', propertyId);
      if (userEmail) params.append('email', encodeURIComponent(userEmail));

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/pdf' },
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `RevenueReport_${pdfPeriod}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('PDF downloaded successfully!');
        setShowPdfModal(false);
      } else {
        throw new Error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) {
      toast.error('Please enter recipient email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailRecipient)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/reports/revenue/send-email`;

      const params = new URLSearchParams();

      // Add export_format - default to pdf
      params.append('export_format', 'pdf');

      // IMPORTANT: Don't encode the email - URLSearchParams handles it
      params.append('recipient_email', emailRecipient);
      params.append('recipient_name', emailRecipientName || 'Hotel Manager');
      params.append('generated_by', emailGeneratedBy || userEmail || 'Hotel Manager');

      // Add period and date parameters
      if (emailPeriod === 'monthly' && emailMonth) {
        params.append('period', 'monthly');
        params.append('year', emailYear);
        params.append('month', emailMonth);
      } else if (emailPeriod === 'yearly') {
        params.append('period', 'yearly');
        params.append('year', emailYear);
      } else if (emailPeriod === 'daily') {
        if (emailStartDate) {
          params.append('period', 'daily');
          params.append('start_date', emailStartDate);
          params.append('end_date', emailStartDate);
        }
      } else if (emailPeriod === 'weekly') {
        if (emailStartDate && emailEndDate) {
          params.append('period', 'weekly');
          params.append('start_date', emailStartDate);
          params.append('end_date', emailEndDate);
        }
      } else if (emailPeriod === 'custom') {
        if (emailStartDate && emailEndDate) {
          params.append('period', 'custom');
          params.append('start_date', emailStartDate);
          params.append('end_date', emailEndDate);
        }
      }

      // Add filters
      if (propertyId) params.append('property_id', propertyId);
      if (userEmail) {
        // Don't encode - URLSearchParams handles it
        params.append('sender_email', userEmail);
      }
      if (emailBillingCategory && emailBillingCategory !== 'All') {
        params.append('billing_category', emailBillingCategory);
      }
      if (emailPaymentMethod && emailPaymentMethod !== 'All') {
        params.append('payment_method', emailPaymentMethod);
      }
      if (emailBookingType && emailBookingType !== 'All') {
        params.append('booking_type', emailBookingType);
      }
      if (emailCcHotel) params.append('cc_hotel', 'true');

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      console.log('Sending email URL:', url); // Debug log
      console.log('Recipient email:', emailRecipient); // Debug log

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Report sent to ${emailRecipient} successfully!`);
        setShowEmailModal(false);
        setEmailRecipient('');
        setEmailRecipientName('');
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email report');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getKPI = () => reportData?.kpi || {};
  const getTax = () => reportData?.tax || {};
  const getOccupancy = () => reportData?.occupancy || {};
  const getRevenueDistribution = () => reportData?.revenue_distribution || [];
  const getBreakdown = () => reportData?.breakdown || {};
  const getPaymentMethods = () => reportData?.payment_methods || [];
  const getWeeklyTrend = () => reportData?.weekly_trend || [];
  const getMonthlyComparison = () => reportData?.monthly_comparison || [];
  const getTopCategories = () => reportData?.top_categories || [];
  const getTransactions = () => reportData?.transactions || [];
  const getRestaurantOrders = () => reportData?.restaurant_orders || [];
  const getFilters = () => reportData?.filters || {};

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#E4E8F0] rounded-xl shadow-lg p-4">
          <p className="text-xs font-semibold text-[#4A5F7A] mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedBillingCategory('All');
    setSelectedPaymentMethod('All');
    setSelectedBookingType('All');
    toast.info('Filters reset');
  };

  // Open Excel Modal
  const openExcelModal = () => {
    setExcelPeriod(selectedPeriod);
    setExcelYear(year);
    setExcelMonth(month);
    setExcelStartDate(startDate);
    setExcelEndDate(endDate);
    setExcelBillingCategory(selectedBillingCategory);
    setExcelPaymentMethod(selectedPaymentMethod);
    setExcelBookingType(selectedBookingType);
    setShowExcelModal(true);
  };

  if (loading && !reportData) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0A1628] border-t-[#C9A84C] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A5F7A] text-sm font-medium">Loading revenue report...</p>
        </div>
      </div>
    );
  }

  const kpi = getKPI();
  const tax = getTax();
  const occupancy = getOccupancy();
  const breakdown = getBreakdown();
  const distribution = getRevenueDistribution();
  const paymentMethods = getPaymentMethods();
  const topCategories = getTopCategories();
  const weeklyTrend = getWeeklyTrend();
  const monthlyComparison = getMonthlyComparison();
  const transactions = getTransactions();
  const restaurantOrders = getRestaurantOrders();
  const filters = getFilters();

  // Prepare data for pie chart
  const pieData = distribution.map((item) => ({
    ...item,
    amount: item.amount || 0,
    percentage: item.percentage || 0,
  }));

  // Category cards data
  const categoryCards = [
    {
      key: 'room_revenue',
      label: 'Room Revenue',
      icon: <FaHotel />,
      color: '#0A1628',
    },
    {
      key: 'food_revenue',
      label: 'Food Revenue',
      icon: <FaUtensils />,
      color: '#C9A84C',
    },
    {
      key: 'amenity_revenue',
      label: 'Amenity Revenue',
      icon: <FaSpa />,
      color: '#1A2A4A',
    },
    {
      key: 'service_revenue',
      label: 'Service Revenue',
      icon: <FaConciergeBell />,
      color: '#2A4580',
    },
    {
      key: 'restaurant_revenue',
      label: 'Restaurant Revenue',
      icon: <FaCoffee />,
      color: '#D4AF37',
    },
    {
      key: 'enhance_stay_revenue',
      label: 'Enhancements',
      icon: <FaPlus />,
      color: '#6B7FA0',
    },
  ];

  const totalRevenue = Object.values(breakdown).reduce((a, b) => a + b, 0);

  const getCategoryColor = (category, index) => {
    const colorMap = {
      'Room Revenue': '#0A1628',
      'Food Revenue': '#C9A84C',
      'Amenity Revenue': '#1A2A4A',
      'Service Revenue': '#2A4580',
      'Restaurant Revenue': '#D4AF37',
      'Enhance Stay Revenue': '#6B7FA0',
    };
    return colorMap[category] || CHART_COLORS[index % CHART_COLORS.length];
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-6 font-sans">
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-r from-[#0A1628] to-[#1A2A4A] rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C9A84C] rounded-2xl flex items-center justify-center shadow-lg">
              <FaChartLine className="text-[#0A1628] text-3xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Revenue Report</h1>
              <p className="text-[#C9A84C] text-sm font-medium mt-0.5">
                {reportData?.month_label || 'Loading...'} • {reportData?.date_from || '-'} to{' '}
                {reportData?.date_to || '-'}
              </p>
              {userEmail && (
                <p className="text-xs text-[#6B7FA0] mt-0.5 flex items-center gap-1">
                  <FaUser size={10} /> {userEmail}
                </p>
              )}
              {Object.entries(filters).some(([key, val]) => val && key !== 'property_id') && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(filters).map(([key, val]) => {
                    if (!val || key === 'property_id') return null;
                    return (
                      <span
                        key={key}
                        className="text-[10px] bg-[#C9A84C]/20 text-[#C9A84C] px-2 py-0.5 rounded-full"
                      >
                        {key.replace('_', ' ')}: {val}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => fetchRevenueReport()}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all text-white text-sm font-medium border border-white/20"
            >
              <FaSync className={loading ? 'animate-spin' : ''} size={12} />
              Refresh
            </button>
            {/* <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all text-white text-sm font-medium border border-white/20"
            >
              <FaPrint size={12} />
              Print
            </button> */}
            <button
              onClick={openExcelModal}
              className="flex items-center gap-2 px-4 py-2 bg-[#0A1628] text-white rounded-xl hover:bg-[#1A2A4A] transition-all text-sm font-medium border border-[#C9A84C]/30 shadow-lg hover:shadow-xl"
            >
              <FaFileExcel size={12} />
              Excel
            </button>
            <button
              onClick={() => {
                setEmailPeriod(selectedPeriod);
                setEmailYear(year);
                setEmailMonth(month);
                setEmailStartDate(startDate);
                setEmailEndDate(endDate);
                setEmailBillingCategory(selectedBillingCategory);
                setEmailPaymentMethod(selectedPaymentMethod);
                setEmailBookingType(selectedBookingType);
                setShowEmailModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#C9A84C] rounded-xl hover:bg-[#B8973A] transition-all text-[#0A1628] text-sm font-medium shadow-lg hover:shadow-xl"
            >
              <FaEnvelope size={12} />
              Email
            </button>
            <button
              onClick={() => {
                setPdfPeriod(selectedPeriod);
                setPdfYear(year);
                setPdfMonth(month);
                setPdfStartDate(startDate);
                setPdfEndDate(endDate);
                setShowPdfModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl hover:bg-[#C9A84C] transition-all text-[#0A1628] text-sm font-medium shadow-lg hover:shadow-xl"
            >
              <FaFilePdf size={12} />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Period Selection - Improved UI */}
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 border border-[#E4E8F0]">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-[#C9A84C]" />
            <span className="text-sm font-medium text-[#4A5F7A]">Period:</span>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="custom">Custom Range</option>
          </select>

          {selectedPeriod === 'monthly' && (
            <>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
              >
                {[
                  'Jan',
                  'Feb',
                  'Mar',
                  'Apr',
                  'May',
                  'Jun',
                  'Jul',
                  'Aug',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dec',
                ].map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </>
          )}

          {selectedPeriod === 'yearly' && (
            <>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[#6B7FA0]">
                (Full year: {year}-01-01 to {year}-12-31)
              </span>
            </>
          )}

          {selectedPeriod === 'daily' && (
            <>
              <input
                type="date"
                value={dailyDate}
                onChange={(e) => handleDateChange('daily', e.target.value)}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!dailyDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  dailyDate
                    ? 'bg-[#C9A84C] text-[#0A1628] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}

          {selectedPeriod === 'weekly' && (
            <>
              <input
                type="date"
                value={weekStartDate}
                onChange={(e) => handleDateChange('weekStart', e.target.value)}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                placeholder="Week Start"
              />
              <span className="text-[#6B7FA0]">to</span>
              <input
                type="date"
                value={weekEndDate}
                onChange={(e) => handleDateChange('weekEnd', e.target.value)}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                placeholder="Week End"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!weekStartDate || !weekEndDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  weekStartDate && weekEndDate
                    ? 'bg-[#C9A84C] text-[#0A1628] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}

          {selectedPeriod === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('customStart', e.target.value)}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                placeholder="Start Date"
              />
              <span className="text-[#6B7FA0]">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('customEnd', e.target.value)}
                className="px-4 py-2 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                placeholder="End Date"
              />
              <button
                onClick={handleApplyDateRange}
                disabled={!startDate || !endDate}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  startDate && endDate
                    ? 'bg-[#C9A84C] text-[#0A1628] hover:bg-[#B8973A] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter Dropdown */}
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border border-[#E4E8F0]">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A1628] text-white rounded-xl hover:bg-[#1A2A4A] transition-all text-sm font-medium"
          >
            <FaFilter size={12} />
            Filters
            {(selectedBillingCategory !== 'All' ||
              selectedPaymentMethod !== 'All' ||
              selectedBookingType !== 'All') && (
              <span className="w-2 h-2 bg-[#C9A84C] rounded-full animate-pulse"></span>
            )}
          </button>

          {showFilterDropdown && (
            <div className="w-full mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-[#F5F7FA] rounded-xl border border-[#E4E8F0] animate-fadeIn">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Billing Category
                </label>
                <select
                  value={selectedBillingCategory}
                  onChange={(e) => setSelectedBillingCategory(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                >
                  {BILLING_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat === 'All' ? 'All Categories' : cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Payment Method
                </label>
                <select
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method === 'All' ? 'All Methods' : method}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider block mb-2">
                  Booking Type
                </label>
                <select
                  value={selectedBookingType}
                  onChange={(e) => setSelectedBookingType(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                >
                  {BOOKING_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type === 'All' ? 'All Types' : type}
                    </option>
                  ))}
                </select>
              </div>
              {(selectedBillingCategory !== 'All' ||
                selectedPaymentMethod !== 'All' ||
                selectedBookingType !== 'All') && (
                <div className="md:col-span-3 flex justify-end">
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2 text-sm font-medium text-[#6B7FA0] hover:text-[#0A1628] transition-colors flex items-center gap-2"
                  >
                    <FaTimes size={12} />
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main KPI Cards - Same as before, keep this section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[#0A1628] to-[#1A2A4A] rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#C9A84C] uppercase tracking-wider">
                Total Revenue
              </p>
              <p className="text-2xl font-bold text-white mt-1.5">
                {formatCurrency(kpi.total_revenue)}
              </p>
              <div className="flex items-center gap-1.5 mt-1.5">
                {kpi.revenue_growth_pct >= 0 ? (
                  <FaArrowUp className="text-emerald-400" size={10} />
                ) : (
                  <FaArrowDown className="text-red-400" size={10} />
                )}
                <span
                  className={`text-xs font-medium ${
                    kpi.revenue_growth_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  {kpi.revenue_growth_pct}% vs last period
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#C9A84C]/20 rounded-2xl flex items-center justify-center">
              <FaRupeeSign className="text-[#C9A84C] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Net Revenue
              </p>
              <p className="text-2xl font-bold text-[#0A1628] mt-1.5">
                {formatCurrency(kpi.net_revenue)}
              </p>
              <p className="text-xs text-[#6B7FA0] mt-1.5">After adjustments</p>
            </div>
            <div className="w-12 h-12 bg-[#0A1628]/10 rounded-2xl flex items-center justify-center">
              <FaWallet className="text-[#0A1628] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Total Collected
              </p>
              <p className="text-2xl font-bold text-[#0A1628] mt-1.5">
                {formatCurrency(kpi.total_collected)}
              </p>
              <p className="text-xs text-[#6B7FA0] mt-1.5">Successful payments</p>
            </div>
            <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-2xl flex items-center justify-center">
              <FaCreditCard className="text-[#C9A84C] text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                Revenue (Excl. Tax)
              </p>
              <p className="text-2xl font-bold text-[#0A1628] mt-1.5">
                {formatCurrency(kpi.total_revenue_without_tax)}
              </p>
              <p className="text-xs text-[#6B7FA0] mt-1.5">Before taxes</p>
            </div>
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center">
              <FaDollarSign className="text-[#D4AF37] text-2xl" />
            </div>
          </div>
        </div>
      </div>
      {/* Category Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {categoryCards.map((card, idx) => {
          const amount = breakdown[card.key] || 0;
          const percentage = totalRevenue > 0 ? (amount / totalRevenue) * 100 : 0;
          return (
            <div
              key={idx}
              className="bg-white rounded-2xl p-4 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${card.color}15` }}
                    >
                      <span style={{ color: card.color }}>{card.icon}</span>
                    </div>
                    <p className="text-xs font-semibold text-[#4A5F7A]">{card.label}</p>
                  </div>
                  <p className="text-lg font-bold text-[#0A1628]">{formatCurrency(amount)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-medium text-[#6B7FA0]">
                      {percentage.toFixed(1)}%
                    </span>
                    <div className="w-12 bg-[#F0F2F5] rounded-full h-1">
                      <div
                        className="rounded-full h-1 transition-all duration-700"
                        style={{
                          width: `${Math.min(percentage, 100)}%`,
                          backgroundColor: card.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0A1628] flex items-center gap-2">
              <FaChartLine className="text-[#C9A84C]" />
              Revenue Trend
            </h3>
          </div>
          {weeklyTrend.some(
            (w) =>
              w.room_revenue > 0 ||
              w.food_revenue > 0 ||
              w.amenity_revenue > 0 ||
              w.restaurant_revenue > 0 ||
              w.enhance_stay_revenue > 0
          ) ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={weeklyTrend} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F5" strokeWidth={0.5} />
                <XAxis
                  dataKey="label"
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(value) => {
                    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
                    return `₹${value}`;
                  }}
                  stroke="#6B7FA0"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 'auto']}
                  tickCount={5}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={40}
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{
                    fontSize: '11px',
                    color: '#4A5F7A',
                    fontWeight: 500,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="room_revenue"
                  stroke="#0A1628"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#0A1628', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Room Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="food_revenue"
                  stroke="#C9A84C"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#C9A84C', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Food Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="amenity_revenue"
                  stroke="#1A2A4A"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#1A2A4A', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Amenity Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="service_revenue"
                  stroke="#2A4580"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#2A4580', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Service Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="restaurant_revenue"
                  stroke="#D4AF37"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#D4AF37', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Restaurant Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="enhance_stay_revenue"
                  stroke="#6B7FA0"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#6B7FA0', strokeWidth: 2 }}
                  activeDot={{ r: 7 }}
                  name="Enhancements"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[320px] text-[#6B7FA0] text-sm font-medium">
              No trend data available
            </div>
          )}
        </div>

        {/* Revenue Distribution */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-[#E4E8F0] hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0A1628] flex items-center gap-2">
              <FaChartPie className="text-[#C9A84C]" />
              Revenue Distribution
            </h3>
          </div>
          {pieData.some((d) => d.amount > 0) ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="relative flex items-center justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData.filter((d) => d.amount > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="amount"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#6B7FA0', strokeWidth: 1 }}
                    >
                      {pieData
                        .filter((d) => d.amount > 0)
                        .map((entry, index) => {
                          const color = getCategoryColor(entry.category, index);
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={color}
                              stroke="white"
                              strokeWidth={3}
                            />
                          );
                        })}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E4E8F0',
                        borderRadius: '12px',
                        padding: '10px 14px',
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div>
                <div className="flex flex-col justify-center gap-2">
                  {pieData.map((item, idx) => {
                    const color = getCategoryColor(item.category, idx);
                    const hasValue = item.amount > 0;
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded-xl transition-all border ${
                          hasValue
                            ? 'hover:bg-[#F5F7FA] hover:border-[#E4E8F0] border-transparent'
                            : 'border-dashed border-[#E4E8F0] bg-[#FAFBFC] opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3.5 h-3.5 rounded-full shadow-sm"
                            style={{
                              backgroundColor: hasValue ? color : '#D1D5DB',
                            }}
                          ></div>
                          <span
                            className={`text-sm font-medium ${hasValue ? 'text-[#0A1628]' : 'text-[#6B7FA0]'}`}
                          >
                            {item.category === 'Enhance Stay Revenue'
                              ? 'Enhancements'
                              : item.category}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-sm font-bold ${hasValue ? 'text-[#0A1628]' : 'text-[#6B7FA0]'}`}
                          >
                            {formatCurrency(item.amount)}
                          </span>
                          <span
                            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                              hasValue
                                ? 'bg-[#F0F2F5] text-[#6B7FA0]'
                                : 'bg-[#F3F4F6] text-[#9CA3AF]'
                            }`}
                          >
                            {item.percentage?.toFixed(1) || '0'}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-[#6B7FA0] text-sm font-medium">
              No distribution data available
            </div>
          )}
        </div>
      </div>

      {/* Revenue Breakdown Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all mb-6">
        <div className="px-6 py-4 bg-gradient-to-r from-[#0A1628] to-[#1A2A4A]">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaChartBar className="text-[#C9A84C]" />
            Revenue Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E4E8F0]">
            <thead>
              <tr className="bg-[#F5F7FA]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Share
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E4E8F0]">
              {distribution.length > 0 ? (
                distribution.map((item, idx) => {
                  const color = getCategoryColor(item.category, idx);
                  const displayName =
                    item.category === 'Enhance Stay Revenue' ? 'Enhancements' : item.category;
                  return (
                    <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628] group-hover:text-[#C9A84C] transition-colors">
                          {displayName}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(item.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-3">
                          <span className="text-sm font-medium text-[#4A5F7A]">
                            {item.percentage?.toFixed(1) || '0'}%
                          </span>
                          <div className="w-24 bg-[#F0F2F5] rounded-full h-2">
                            <div
                              className="rounded-full h-2 transition-all duration-700"
                              style={{
                                width: `${Math.min(item.percentage || 0, 100)}%`,
                                backgroundColor: color,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No breakdown data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Methods and Tax */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all">
          <div className="px-6 py-4 bg-[#C9A84C]">
            <h3 className="text-sm font-bold text-[#0A1628] flex items-center gap-2">
              <FaCreditCard className="text-[#0A1628]" />
              Payment Methods
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E4E8F0]">
              <thead>
                <tr className="bg-[#F5F7FA]">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E4E8F0]">
                {paymentMethods.length > 0 ? (
                  paymentMethods.map((method, idx) => (
                    <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628] group-hover:text-[#C9A84C] transition-colors">
                          {method.method}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(method.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-3">
                          <span className="text-sm font-medium text-[#4A5F7A]">
                            {method.percentage?.toFixed(1) || '0'}%
                          </span>
                          <div className="w-20 bg-[#F0F2F5] rounded-full h-2">
                            <div
                              className="rounded-full h-2 transition-all duration-700"
                              style={{
                                width: `${Math.min(method.percentage || 0, 100)}%`,
                                backgroundColor: '#0A1628',
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                    >
                      No payment data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tax Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all">
          <div className="px-6 py-4 bg-[#1A2A4A]">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <FaPercent className="text-[#C9A84C]" />
              Tax Breakdown
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E4E8F0]">
              <thead>
                <tr className="bg-[#F5F7FA]">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    Tax Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    CGST
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    SGST
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E4E8F0]">
                {tax.total_tax_collected > 0 ? (
                  <>
                    <tr className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628]">Room Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.room_cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.room_sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(tax.room_tax || 0)}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628]">Food Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.food_cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.food_sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(tax.food_tax || 0)}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628]">Service Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.service_cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.service_sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(tax.service_tax || 0)}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628]">Restaurant Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.restaurant_cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.restaurant_sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(tax.restaurant_tax || 0)}
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628]">Enhancements Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.enhance_stay_cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm text-[#4A5F7A]">
                          {formatCurrency(tax.enhance_stay_sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(tax.enhance_stay_tax || 0)}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-[#F5F7FA] font-semibold border-t-2 border-[#E4E8F0]">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-bold text-[#0A1628]">Total Tax</span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#C9A84C]">
                          {formatCurrency(tax.cgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#C9A84C]">
                          {formatCurrency(tax.sgst || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#C9A84C]">
                          {formatCurrency(tax.total_tax_collected || 0)}
                        </span>
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                    >
                      No tax data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top Categories */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all mb-6">
        <div className="px-6 py-4 bg-gradient-to-r from-[#0A1628] to-[#1A2A4A]">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaTrophy className="text-[#C9A84C]" />
            Top Revenue Categories
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E4E8F0]">
            <thead>
              <tr className="bg-[#F5F7FA]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Avg Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E4E8F0]">
              {topCategories.length > 0 ? (
                topCategories.map((category, idx) => (
                  <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-[#0A1628] group-hover:text-[#C9A84C] transition-colors">
                          {category.category === 'Enhance stay reservation'
                            ? 'Enhancements'
                            : category.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-bold text-[#0A1628]">
                        {formatCurrency(category.revenue)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {category.units_bookings || 0}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A] bg-[#F0F2F5] px-3 py-1 rounded-full">
                        {formatCurrency(category.average_value)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No category data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Comparison */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all mb-6">
        <div className="px-6 py-4 bg-gradient-to-r from-[#1A2A4A] to-[#2A4580]">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaCalendarAlt className="text-[#C9A84C]" />
            Monthly Comparison
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E4E8F0]">
            <thead>
              <tr className="bg-[#F5F7FA]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Month
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Current Year
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Previous Year
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Growth
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E4E8F0]">
              {monthlyComparison.some((m) => m.current_year > 0 || m.previous_year > 0) ? (
                monthlyComparison.map((item, idx) => {
                  const growth =
                    item.previous_year > 0
                      ? ((item.current_year - item.previous_year) / item.previous_year) * 100
                      : item.current_year > 0
                        ? 100
                        : 0;
                  return (
                    <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                      <td className="px-6 py-3.5 whitespace-nowrap">
                        <span className="text-sm font-medium text-[#0A1628] group-hover:text-[#C9A84C] transition-colors">
                          {item.month}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-[#0A1628]">
                          {formatCurrency(item.current_year)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-medium text-[#4A5F7A]">
                          {formatCurrency(item.previous_year)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                            growth >= 0
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {growth >= 0 ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                          {growth.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No monthly comparison data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Restaurant Orders Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all mb-6">
        <div className="px-6 py-4 bg-gradient-to-r from-[#D4AF37] to-[#C9A84C]">
          <h3 className="text-sm font-bold text-[#0A1628] flex items-center gap-2">
            <FaCoffee className="text-[#0A1628]" />
            Restaurant Orders
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E4E8F0]">
            <thead>
              <tr className="bg-[#F5F7FA]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Payment ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Base Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Tax
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E4E8F0]">
              {restaurantOrders.length > 0 ? (
                restaurantOrders.map((order, idx) => (
                  <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#0A1628] group-hover:text-[#C9A84C] transition-colors">
                        {order.order_id?.substring(0, 20)}...
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm text-[#4A5F7A]">
                        {order.payment_id?.substring(0, 20)}...
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#0A1628] capitalize">
                        {order.payment_method}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {formatCurrency(order.base_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {formatCurrency(order.tax_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-bold text-[#0A1628]">
                        {formatCurrency(order.amount)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No restaurant orders available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#E4E8F0] overflow-hidden hover:shadow-xl transition-all">
        <div className="px-6 py-4 bg-gradient-to-r from-[#2A4580] to-[#4A6A9A]">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaList className="text-[#C9A84C]" />
            Transactions
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E4E8F0]">
            <thead>
              <tr className="bg-[#F5F7FA]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Tax
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider">
                  Final
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E4E8F0]">
              {transactions.length > 0 ? (
                transactions.map((transaction, idx) => (
                  <tr key={idx} className="hover:bg-[#F5F7FA] transition-all group">
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#0A1628]">
                        {transaction.booking_id}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm text-[#4A5F7A] group-hover:text-[#C9A84C] transition-colors">
                        {transaction.category === 'Enhance stay reservation'
                          ? 'Enhancements'
                          : transaction.category}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span className="text-sm text-[#4A5F7A]">{transaction.payment_method}</span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                          transaction.booking_type === 'Walkin'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {transaction.booking_type}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {formatCurrency(transaction.base_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-[#4A5F7A]">
                        {formatCurrency(transaction.tax_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right">
                      <span className="text-sm font-bold text-[#0A1628]">
                        {formatCurrency(transaction.final_amount)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-8 text-center text-[#6B7FA0] text-sm font-medium"
                  >
                    No transactions available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF Download Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0A1628] flex items-center gap-2">
                <FaFilePdf className="text-[#C9A84C]" />
                Download PDF Report
              </h2>
              <button
                onClick={() => setShowPdfModal(false)}
                className="text-[#6B7FA0] hover:text-[#0A1628] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                  <FaCalendarAlt size={12} />
                  Period
                </label>
                <select
                  value={pdfPeriod}
                  onChange={(e) => setPdfPeriod(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {pdfPeriod === 'custom' ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={(e) => setPdfStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={(e) => setPdfEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                </>
              ) : pdfPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={pdfStartDate}
                    onChange={(e) => setPdfStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                  />
                </div>
              ) : pdfPeriod === 'weekly' ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={(e) => setPdfStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={(e) => setPdfEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={pdfYear}
                      onChange={(e) => setPdfYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {pdfPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={pdfMonth}
                        onChange={(e) => setPdfMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {userEmail && (
                <div className="bg-[#F5F7FA] rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#4A5F7A]">User</span>
                    <span className="font-medium text-[#0A1628] truncate max-w-[180px]">
                      {userEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPdfModal(false)}
                className="flex-1 py-3 border-2 border-[#E4E8F0] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0A1628]"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={loading}
                className="flex-1 py-3 bg-[#0A1628] text-white rounded-xl hover:bg-[#1A2A4A] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Download Modal */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0A1628] flex items-center gap-2">
                <FaFileExcel className="text-green-600" />
                Download Excel Report
              </h2>
              <button
                onClick={() => setShowExcelModal(false)}
                className="text-[#6B7FA0] hover:text-[#0A1628] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                  <FaCalendarAlt size={12} />
                  Period
                </label>
                <select
                  value={excelPeriod}
                  onChange={(e) => setExcelPeriod(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {excelPeriod === 'custom' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={excelStartDate}
                      onChange={(e) => setExcelStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={excelEndDate}
                      onChange={(e) => setExcelEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                </div>
              ) : excelPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={excelStartDate}
                    onChange={(e) => setExcelStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                  />
                </div>
              ) : excelPeriod === 'weekly' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={excelStartDate}
                      onChange={(e) => setExcelStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={excelEndDate}
                      onChange={(e) => setExcelEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={excelYear}
                      onChange={(e) => setExcelYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {excelPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={excelMonth}
                        onChange={(e) => setExcelMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Excel Filters Section - 2 Column Grid */}
              <div className="border-t border-[#E4E8F0] pt-4">
                <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-3">
                  <FaFilter size={12} />
                  Filters
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">
                      Billing Category
                    </label>
                    <select
                      value={excelBillingCategory}
                      onChange={(e) => setExcelBillingCategory(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Room Rent">Room Rent</option>
                      <option value="Food Order">Food Order</option>
                      <option value="Amenity Service">Amenity Service</option>
                      <option value="Room Service">Room Service</option>
                      <option value="Enhance stay reservation">Enhance stay reservation</option>
                      <option value="Enhance stay modification">Enhance stay modification</option>
                      <option value="Restaurant POS">Restaurant POS</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Payment Method</label>
                    <select
                      value={excelPaymentMethod}
                      onChange={(e) => setExcelPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Netbanking">Netbanking</option>
                      <option value="Razorpay">Razorpay</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Type</label>
                    <select
                      value={excelBookingType}
                      onChange={(e) => setExcelBookingType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Walkin">Walkin</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>
                </div>
              </div>

              {userEmail && (
                <div className="bg-[#F5F7FA] rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#4A5F7A]">User</span>
                    <span className="font-medium text-[#0A1628] truncate max-w-[180px]">
                      {userEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowExcelModal(false)}
                className="flex-1 py-3 border-2 border-[#E4E8F0] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0A1628]"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadExcel}
                disabled={loading}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg hover:shadow-xl"
              >
                {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                {loading ? 'Generating...' : 'Download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#0A1628] flex items-center gap-2">
                <FaEnvelope className="text-[#C9A84C]" />
                Send Report via Email
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-[#6B7FA0] hover:text-[#0A1628] transition p-2 rounded-lg hover:bg-[#F0F2F5]"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaEnvelope size={12} />
                    Recipient Email *
                  </label>
                  <input
                    type="email"
                    placeholder="Enter recipient email"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value.trim())}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaUser size={12} />
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter recipient name"
                    value={emailRecipientName}
                    onChange={(e) => setEmailRecipientName(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaUser size={12} />
                    Generated By
                  </label>
                  <input
                    type="text"
                    value={emailGeneratedBy}
                    disabled={true}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl text-sm text-[#0A1628] font-medium bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Period
                  </label>
                  <select
                    value={emailPeriod}
                    onChange={(e) => setEmailPeriod(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
              </div>

              {emailPeriod === 'custom' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={emailStartDate}
                      onChange={(e) => setEmailStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={emailEndDate}
                      onChange={(e) => setEmailEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                    />
                  </div>
                </div>
              ) : emailPeriod === 'daily' ? (
                <div>
                  <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                    <FaCalendarAlt size={12} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={emailStartDate}
                    onChange={(e) => setEmailStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                  />
                </div>
              ) : emailPeriod === 'weekly' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week Start
                    </label>
                    <input
                      type="date"
                      value={emailStartDate}
                      onChange={(e) => setEmailStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Week End
                    </label>
                    <input
                      type="date"
                      value={emailEndDate}
                      onChange={(e) => setEmailEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                      <FaCalendarAlt size={12} />
                      Year
                    </label>
                    <select
                      value={emailYear}
                      onChange={(e) => setEmailYear(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        (y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  {emailPeriod === 'monthly' && (
                    <div>
                      <label className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={12} />
                        Month
                      </label>
                      <select
                        value={emailMonth}
                        onChange={(e) => setEmailMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 border-2 border-[#E4E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-sm bg-white text-[#0A1628] font-medium transition-all"
                      >
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-[#E4E8F0] pt-4">
                <p className="text-xs font-semibold text-[#4A5F7A] uppercase tracking-wider flex items-center gap-2 mb-3">
                  <FaFilter size={12} />
                  Additional Filters
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Export Format</label>
                    <select
                      value={emailExportFormat}
                      onChange={(e) => setEmailExportFormat(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="pdf">PDF</option>
                      <option value="xlsx">Excel</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">
                      Billing Category
                    </label>
                    <select
                      value={emailBillingCategory}
                      onChange={(e) => setEmailBillingCategory(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Room Rent">Room Rent</option>
                      <option value="Food Order">Food Order</option>
                      <option value="Amenity Service">Amenity Service</option>
                      <option value="Room Service">Room Service</option>
                      <option value="Enhance stay reservation">Enhance stay reservation</option>
                      <option value="Enhance stay modification">Enhance stay modification</option>
                      <option value="Restaurant POS">Restaurant POS</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Payment Method</label>
                    <select
                      value={emailPaymentMethod}
                      onChange={(e) => setEmailPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Netbanking">Netbanking</option>
                      <option value="Razorpay">Razorpay</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#6B7FA0]">Booking Type</label>
                    <select
                      value={emailBookingType}
                      onChange={(e) => setEmailBookingType(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-[#E4E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] text-xs bg-white text-[#0A1628] font-medium transition-all mt-1"
                    >
                      <option value="">All</option>
                      <option value="Walkin">Walkin</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <div className="w-full">
                      <label className="text-[10px] font-medium text-[#6B7FA0] block mb-1">
                        CC Hotel
                      </label>
                      <button
                        type="button"
                        onClick={() => setEmailCcHotel(!emailCcHotel)}
                        className={`relative w-full h-10 rounded-xl transition-all duration-300 flex items-center px-3 ${
                          emailCcHotel
                            ? 'bg-[#C9A84C] text-white shadow-md'
                            : 'bg-[#E4E8F0] text-[#6B7FA0]'
                        }`}
                      >
                        <span className="flex items-center justify-between w-full">
                          <span className="text-xs font-medium flex items-center gap-2">
                            {emailCcHotel ? (
                              <>
                                <FaCheck size={12} />
                                CC Enabled
                              </>
                            ) : (
                              <>
                                <FaTimes size={12} />
                                CC Disabled
                              </>
                            )}
                          </span>
                          <span className="w-6 h-6 rounded-full bg-white shadow-md" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 py-3 border-2 border-[#E4E8F0] rounded-xl hover:bg-[#F5F7FA] transition text-sm font-medium text-[#0A1628]"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 py-3 bg-[#C9A84C] text-[#0A1628] rounded-xl hover:bg-[#B8973A] transition flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 shadow-lg hover:shadow-xl"
              >
                {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                {sendingEmail ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media print {
          .bg-[#F5F7FA] {
            background: white !important;
          }
          button,
          .shadow-lg,
          .shadow-xl {
            box-shadow: none !important;
          }
          .fixed {
            display: none !important;
          }
          .border {
            border-color: #e4e8f0 !important;
          }
          .bg-gradient-to-r {
            background: #0a1628 !important;
          }
        }
        * {
          font-family:
            -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
            sans-serif;
        }
        .hover\\:shadow-xl:hover {
          box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
        }
        .hover\\:-translate-y-1:hover {
          transform: translateY(-4px);
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RevenueReport;
